import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedDate } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';

import ProductDetails from 'Helpers/Product/ProductDetails';

export default function DetailItem({ type, title, items, item, shortDate, tooltipText }) {
  const [showTooltip, setShowTooltip] = useState(false);

  const closeTooltip = (ev) => {
    if (ev.key === 'Escape') {
      setShowTooltip(false);
    }
  };

  useEffect(() => {
    if (tooltipText) {
      document.addEventListener('keydown', closeTooltip);
    }
    return () => {
      document.removeEventListener('keydown', closeTooltip);
    };
  }, [tooltipText]);

  const tooltipOn = () => {
    setShowTooltip(true);
  };

  const tooltipOff = () => {
    setShowTooltip(false);
  };

  // Tooltips need to have tooltipText passed to them
  const tooltipCheck = () => {
    if (!tooltipText) return false;
    return (
      <div className="DetailItem__toolTip">
        {showTooltip &&
          <div className="DetailItem__toolTip__text" role="tooltip" aria-hidden={!showTooltip}>
            {tooltipText}
          </div>
        }

        <button
          className="DetailItem__toolTip__button"
          aria-label="Tooltip. Expand for more details"
          onMouseEnter={tooltipOn}
          onMouseLeave={tooltipOff}
          onFocus={tooltipOn}
          onBlur={tooltipOff}
        >
          <FontAwesomeIcon icon={faCircleQuestion} />
        </button>
      </div>
    );
  };

  const formatSingleItem = () => {
    if (type === 'date' && shortDate) {
      return <span><FormattedDate value={item} year="numeric" />{tooltipCheck()}</span>;
    }
    if (type === 'date' && !shortDate) {
      return <span><FormattedDate value={item} year="numeric" month="long" day="2-digit" />{tooltipCheck()}</span>;
    }
    return <span>{item}{tooltipCheck()}</span>;
  };

  const commaCheck = (index) => {
    if (index + 1 < items.length) return ',';
    return false;
  };

  return (
    <div className="DetailItem">
      <div className="DetailItem__main">
        {title}
      </div>
      <div className={classNames('DetailItem__support', { 'DetailItem__support--list': type === 'categories' || type === 'features' })}>
        {items.map((i, index) => {
          let mapItem = i;
          if (type === 'platform') mapItem = ProductDetails.formatPlatform(i);
          if (type === 'xboxPlatforms') mapItem = ProductDetails.formatXboxPlatform(i);
          if (type === 'publishers' || type === 'franchises' || type === 'categories') {
            if (typeof i !== 'string' && i.link) {
              return <Link to={`/${type}/${i.slug}`} key={i.name}>{i.name || i.slug}{commaCheck(index)}</Link>;
            }
            if (typeof i !== 'string' && !i.link) {
              return <span key={i.name}>{i.name}{commaCheck(index)}</span>;
            }
            return <span key={mapItem}>{mapItem}{commaCheck(index)}</span>;
          }

          if (!mapItem) return false;

          return (
            <span key={mapItem}>{mapItem}{commaCheck(index)}</span>
          );
        })}

        {item && formatSingleItem()}
      </div>
    </div>
  );
}

DetailItem.propTypes = {
  type: PropTypes.string,
  title: PropTypes.string,
  items: PropTypes.array,
  item: PropTypes.string,
  shortDate: PropTypes.bool,
  tooltipText: PropTypes.string,
};

DetailItem.defaultProps = {
  type: '',
  title: '',
  items: [],
  item: '',
  shortDate: false,
  tooltipText: '',
};
